import { Link } from "react-router-dom";
import { Phone, MapPin, Facebook, Instagram, Youtube, Mail } from "lucide-react";
import { useSite } from "@/lib/useSite";
import { scrollToId, scrollToTop } from "@/lib/api";

export default function Footer() {
  const { data } = useSite();
  const s = data?.settings || {};
  const products = data?.products || [];
  const socials = s.socials || {};
  const phone = s.phone || "6366634517";

  return (
    <footer data-testid="site-footer" className="bg-ink text-white">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <button onClick={scrollToTop} className="flex items-center gap-4 text-left" aria-label="Back to top">
              <img src="/assets/logo-white.png" alt="SRS logo" className="h-12 w-12 object-contain" />
              <div>
                <p className="font-display text-xl font-extrabold tracking-[0.14em]">SRS</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Sun Roofing Solutions</p>
              </div>
            </button>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
              Premium roofing, fencing and structural materials — crafted to last, supplied from Mandya to the region.
            </p>
            <div className="mt-7 flex gap-3">
              {socials.facebook && (
                <a data-testid="social-facebook" href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-srs hover:bg-srs">
                  <Facebook size={16} />
                </a>
              )}
              {socials.instagram && (
                <a data-testid="social-instagram" href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-srs hover:bg-srs">
                  <Instagram size={16} />
                </a>
              )}
              {socials.youtube && (
                <a data-testid="social-youtube" href={socials.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-srs hover:bg-srs">
                  <Youtube size={16} />
                </a>
              )}
              {s.email && (
                <a data-testid="social-email" href={`mailto:${s.email}`} aria-label="Email" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-srs hover:bg-srs">
                  <Mail size={16} />
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">Solutions</p>
            <ul className="space-y-2.5">
              {products.map((p) => (
                <li key={p.id}>
                  <Link to={`/products/${p.slug}`} className="text-sm text-white/70 transition-colors hover:text-srs">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">Explore</p>
            <ul className="space-y-2.5">
              {[
                ["why", "Why SRS"],
                ["stories", "Stories"],
                ["projects", "Portfolio"],
                ["contact", "Contact"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button data-testid={`footer-link-${id}`} onClick={() => scrollToId(id)} className="text-sm text-white/70 transition-colors hover:text-srs">
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">Contact</p>
            <a href={`tel:+91${phone}`} className="flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-srs">
              <Phone size={14} className="text-srs" /> +91 {phone}
            </a>
            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-white/60">
              <MapPin size={14} className="mt-1 shrink-0 text-srs" />
              {s.address || "Karimane Gate, Sugar Factory Road, Mandya, Karnataka"}
            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-14">
          <p className="max-w-4xl font-display text-4xl font-light leading-[1.1] tracking-tight text-white sm:text-6xl">
            Quality today. <em className="italic text-white/60">Stronger tomorrow.</em>
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            © {new Date().getFullYear()} SRS – Sun Roofing Solutions · Mandya, Karnataka
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            Quality today. Stronger tomorrow.
          </p>
        </div>
      </div>
    </footer>
  );
}
